// Claude: add/edit seller as full page — replaced SellerFormModal 25-Apr-2026
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box, Button, Divider, Flex, FormControl, FormLabel, Grid, GridItem,
  Input, Switch, Text, Textarea,
} from "@chakra-ui/react";
import { AutoComplete, Modal, Select } from "antd";
import moment from "moment-timezone";
import { MdArrowBack } from "react-icons/md";
import { AdminSeller } from "../../Interface/AdminInterfaces";
import { AdminAPI } from "../../Apis/adminAPI";
import { AdminSellerAPI } from "../../Apis/adminSellerAPI";
import { DepartmentsAPI } from "../../Apis/departmentsAPI";
import { filterCountries } from "../../Utils/countries";
import { validateEmail } from "../../Utils/Validator";
import { runVerificationCheck } from "../../Utils/verificationCheck";
import { errorHandler } from "../../Utils/handler";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import AdminDigitalAssets from "./AdminDigitalAssets";
import AdminSellerSubscriptionSection from "./SellerModals/AdminSellerSubscriptionSection";
import SellerRenewModal, { SellerRenewData } from "./SellerModals/SellerRenewModal";
import SellerOverrideModal from "./SellerModals/SellerOverrideModal";
import Loader from "../../Components/Loader/Loader";

const COUNTRY_OPTIONS = filterCountries("").map((c: any) => ({ label: c.value, value: c.value }));
const timezones = moment.tz.names();
const mandatoryFields = ["companyName", "personName", "personEmail"];

const emptyForm: AdminSeller = {
  eventId: "", companyName: "", companyHqCity: "", companyHqState: "",
  companyHqCountry: "", companyUrl: "", personName: "", personEmail: "",
  personPhoneNumber: "", personDesignation: "", companyTimezone: "",
  sellerBoothNumberIdentifier: [], productTypeTags: [],
  subscriptionPlanId: "", rewardPlanId: "", industryIds: [], targetIndustryIds: [],
  targetCountries: [], targetDepartments: [],
  companyInfo: "", sellerCompanyType: [], sellerCompanyTypeOther: "", adminNotes: "",
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <GridItem colSpan={3}>
    <Box mt={2} mb={1}>
      <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
        {children}
      </Text>
      <Divider mt={1} borderColor="gray.200" />
    </Box>
  </GridItem>
);

const SellerEditPage = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const isEdit = !!sellerId;
  const location = useLocation();
  const navigate = useNavigate();
  const { error, success } = useToastWrapper();
  const { Option } = Select;

  const searchState = (location.state as any)?.searchState;
  const editOriginalPlanId: string | null = (location.state as any)?.formData?.subscriptionPlanId ?? null;

  const [formData, setFormData] = useState<AdminSeller>(
    (location.state as any)?.formData ?? { ...emptyForm }
  );
  const [sellerEventIds, setSellerEventIds] = useState<string[]>([]);
  const [currentSellerPlan, setCurrentSellerPlan] = useState<any>({});
  const [countryOptions, setCountryOptions] = useState<{ value: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const [eventData, setEventData] = useState<any[]>([]);
  const [subscriptionsData, setSubscriptionsData] = useState<any[]>([]);
  const [allSubscriptionPlans, setAllSubscriptionPlans] = useState<any[]>([]);
  const [rewardPlansData, setRewardPlansData] = useState<any[]>([]);
  const [industryNodes, setTaxonomyNodes] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);

  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewInitialPlanId, setRenewInitialPlanId] = useState<string | undefined>(undefined);
  const [renewLoading, setRenewLoading] = useState(false);

  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
  const [overrideSellerId, setOverrideSellerId] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideNotes, setOverrideNotes] = useState<any[]>([]);
  const [productTagOptions, setProductTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [domainConflictEmail, setDomainConflictEmail] = useState<string | null>(null);
  const [overrideCurrentStatus, setOverrideCurrentStatus] = useState("");
  const [newAdminNote, setNewAdminNote] = useState("");

  const handleChange = (e: any) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const goBack = () => navigate('/admin/sellers', { state: { restoredSearch: searchState } });

  const validate = () => {
    if (mandatoryFields.some(f => !formData[f as keyof AdminSeller])) {
      error("Please fill all the mandatory fields", ""); return false;
    }
    if (!validateEmail(formData.personEmail)) { error("Please enter valid email", ""); return false; }
    if (isEdit && !newAdminNote.trim()) { error("Please enter admin notes", ""); return false; }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload: any = { ...formData };
    if (!payload.eventId) delete payload.eventId;
    if (isEdit) payload.eventIds = sellerEventIds;
    if (!isEdit) { delete payload.subscriptionPlanId; delete payload.rewardPlanId; } // Claude: not assignable on Add — empty strings break ObjectId cast 27-04-2026

    if (isEdit) {
      const timestamp = moment().format("DD/MM/YYYY HH:mm");
      payload.adminNotes = `[${timestamp}] ${newAdminNote.trim()}${formData.adminNotes ? `\n\n${formData.adminNotes}` : ""}`;
      setLoading(true);
      try {
        const res = await AdminAPI.putSeller(sellerId, payload);
        success("Seller edited successfully", "");
        if (res?.data?.verificationTriggered) {
          setOverrideSellerId(res.data.sellerId);
          setOverrideStatus(""); setOverrideReason(""); setOverrideNotes([]); setDomainConflictEmail(null);
          setOverrideCurrentStatus("");
          setIsOverrideModalOpen(true);
        } else {
          goBack();
        }
      } catch (err) { error(errorHandler(err)); }
      finally { setLoading(false); }
    } else {
      const verifResult = runVerificationCheck(formData.personEmail, formData.companyUrl);
      const doCreate = async () => {
        setLoading(true);
        try {
          await AdminAPI.postSeller(payload);
          success("Seller added successfully", "");
          goBack();
        } catch (err) { error(errorHandler(err)); }
        finally { setLoading(false); }
      };
      if (verifResult.status === "unverified") {
        Modal.confirm({
          title: "Verification Warning",
          content: `This seller will be created as unverified. Reason: ${verifResult.reason}. Continue?`,
          okText: "Continue", cancelText: "Cancel", onOk: doCreate,
        });
        return;
      }
      await doCreate();
    }
  };

  const handleOverrideVerification = async () => {
    if (!overrideReason.trim()) { error("Please enter a reason for the override"); return; }
    setLoading(true);
    try {
      await AdminAPI.overrideSellerVerification(overrideSellerId, overrideStatus, overrideReason);
      success("Verification status updated");
      setIsOverrideModalOpen(false);
      goBack();
    } catch (err) { error(errorHandler(err)); }
    finally { setLoading(false); }
  };

  const handleConfirmSellerRenew = async (data: SellerRenewData) => {
    if (!sellerId) return;
    setRenewLoading(true);
    try {
      await AdminSellerAPI.renewSellerSubscription(sellerId, { subscriptionPlanId: data.subscriptionPlanId });
      const plan = allSubscriptionPlans.find((p: any) => p._id === data.subscriptionPlanId);
      await AdminSellerAPI.addSellerPaymentRecord(sellerId, {
        date: data.date,
        paymentReference: data.paymentReference,
        planName: data.planName,
        planAmount: plan?.subscriptionValue ?? 0,
        paidAmount: parseFloat(data.paidAmount) || 0,
        rfxAdded: plan?.rfxLimit ?? 0,
      });
      success("Subscription renewed successfully", "");
      setIsRenewModalOpen(false);
    } catch (err) { error(errorHandler(err)); }
    finally { setRenewLoading(false); }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [ev, subs, tree, reward, depts, tags] = await Promise.all([
          AdminAPI.getEvent(),
          AdminAPI.getSellerSubscriptions(),
          AdminAPI.getIndustryTree(),
          AdminAPI.getRewardPlans(),
          DepartmentsAPI.getAll(),
          AdminAPI.getSellerProductTags(),
        ]);
        if (ev?.data?.data?.length > 0)
          setEventData(ev.data.data.map((e: any) => ({ label: e.eventName, value: e._id })));
        if (subs?.data?.data?.subscriptions?.length > 0) {
          setSubscriptionsData(subs.data.data.subscriptions.map((p: any) => ({ label: p.planName, value: p._id })));
          setAllSubscriptionPlans(subs.data.data.subscriptions);
        }
        setTaxonomyNodes(Array.isArray(tree) ? tree : (tree?.data ?? []));
        const deptList: any[] = Array.isArray(depts) ? depts : (depts?.data ?? []);
        setDepartmentOptions(deptList.map((d: any) => ({ label: d.name, value: String(d._id) })));
        if (reward?.data?.data?.length > 0)
          setRewardPlansData(reward.data.data.map((p: any) => ({ label: p.name, value: p._id })));
        const tagList: string[] = Array.isArray(tags) ? tags : (tags?.data ?? []);
        setProductTagOptions(tagList.map((t: string) => ({ label: t, value: t })));
      } catch (err) { error(errorHandler(err)); }
    };

    const loadEditData = async () => {
      if (!sellerId) return;
      try {
        const [evIds, plan] = await Promise.all([
          AdminAPI.getSellerEventIds(sellerId),
          AdminAPI.getSellerProfile(sellerId),
        ]);
        setSellerEventIds(evIds?.data?.data ?? []);
        if (plan) setCurrentSellerPlan(plan?.data?.data);
      } catch (err) { console.log(err); }
    };

    loadData();
    if (isEdit) loadEditData();
  }, []);

  const selectedPlanPreview = formData.subscriptionPlanId
    ? allSubscriptionPlans.find((p: any) => p._id === formData.subscriptionPlanId) ?? null
    : null;

  return (
    <div className="p-4">
      {loading ? <Loader /> : null}

      <SellerRenewModal
        isOpen={isRenewModalOpen}
        onClose={() => {
          setIsRenewModalOpen(false);
          setFormData(p => ({ ...p, subscriptionPlanId: editOriginalPlanId ?? "" }));
        }}
        sellerId={sellerId ?? ""}
        plansData={allSubscriptionPlans}
        initialPlanId={renewInitialPlanId}
        onConfirm={handleConfirmSellerRenew}
        loading={renewLoading}
      />

      <SellerOverrideModal
        isOpen={isOverrideModalOpen}
        onClose={() => { setIsOverrideModalOpen(false); goBack(); }}
        overrideNotes={overrideNotes}
        overrideSellerId={overrideSellerId}
        overrideStatus={overrideStatus}
        setOverrideStatus={setOverrideStatus}
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        domainConflictEmail={domainConflictEmail}
        setDomainConflictEmail={setDomainConflictEmail}
        overrideCurrentStatus={overrideCurrentStatus}
        onConfirm={handleOverrideVerification}
        loading={loading}
      />

      <Flex mb={4} alignItems="center" gap={3}>
        <Button size="sm" h="28px" fontSize="xs" variant="outline" borderColor="#0E122F" color="#0E122F"
          leftIcon={<MdArrowBack />} onClick={goBack}
        >
          Back
        </Button>
        <Text fontSize="md" fontWeight={600}>
          {isEdit ? `Edit Seller${formData.companyName ? ` — ${formData.companyName}` : ""}` : "Add Seller"}
        </Text>
      </Flex>

      <Box padding={2} bg="white" borderRadius="md" border="1px solid #eee">
        <Grid gap={2} templateColumns="repeat(3, 1fr)">

          <SectionLabel>Company</SectionLabel>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Name <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company Name"
                name="companyName" value={formData.companyName} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company HQ Country</Text>
              <AutoComplete
                style={{ width: "100%", height: "28px" }}
                placeholder="Enter Company HQ Country"
                value={formData.companyHqCountry}
                options={countryOptions}
                onSearch={(q) => setCountryOptions(filterCountries(q))}
                onChange={(val) => setFormData(prev => ({ ...prev, companyHqCountry: val ?? "" }))}
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company HQ State</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company HQ State"
                name="companyHqState" value={formData.companyHqState} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company HQ City</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company HQ City"
                name="companyHqCity" value={formData.companyHqCity} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company URL</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Company URL"
                name="companyUrl" value={formData.companyUrl} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Timezone</Text>
              <Select
                showSearch placeholder="Select Company Timezone" optionFilterProp="children"
                value={formData.companyTimezone}
                onChange={(value) => setFormData({ ...formData, companyTimezone: value })}
                style={{ width: "100%", height: "28px" }}
              >
                {timezones.map((tz) => (<Option key={tz} value={tz}>{tz}</Option>))}
              </Select>
            </Box>
          </GridItem>

          <GridItem colSpan={3}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Info</Text>
              <Textarea placeholder="Enter company description" name="companyInfo"
                value={formData.companyInfo} onChange={handleChange} rows={3} resize="none" />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Industry</Text>
              <Select
                mode="multiple" showSearch optionFilterProp="label"
                style={{ width: "100%" }} placeholder="Select industries"
                value={formData.industryIds}
                onChange={(values) => setFormData({ ...formData, industryIds: values })}
                options={industryNodes.filter((n: any) => !n.parentId).map((n: any) => ({ label: n.name, value: n._id }))}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Target Industry</Text>
              <Select
                mode="multiple" showSearch optionFilterProp="label"
                style={{ width: "100%" }} placeholder="Select target industries"
                value={formData.targetIndustryIds}
                onChange={(values) => setFormData({ ...formData, targetIndustryIds: values })}
                options={industryNodes.filter((n: any) => !n.parentId).map((n: any) => ({ label: n.name, value: n._id }))}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            {isEdit && (
              <Box display="flex" flexDirection="column" gap="4px">
                <Text fontSize="xs" fontWeight="500">Visibility Mode</Text>
                <Text fontSize="xs" textTransform="capitalize" px={1} py={1}>{formData.visibilityMode || "open"}</Text>
              </Box>
            )}
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Target Countries</Text>
              <Select
                mode="multiple" showSearch optionFilterProp="label"
                style={{ width: "100%" }} placeholder="Any country"
                value={formData.targetCountries ?? []}
                onChange={(values) => setFormData({ ...formData, targetCountries: values })}
                options={COUNTRY_OPTIONS}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Target Departments</Text>
              <Select
                mode="multiple" showSearch optionFilterProp="label"
                style={{ width: "100%" }} placeholder="Any department"
                value={formData.targetDepartments ?? []}
                onChange={(values) => setFormData({ ...formData, targetDepartments: values })}
                options={departmentOptions}
                allowClear
              />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Verified Buyers Only</Text>
              <FormControl display="flex" alignItems="center" mt={1}>
                <Switch
                  colorScheme="green"
                  isChecked={formData.targetVerifiedOnly ?? false}
                  onChange={(e) => setFormData({ ...formData, targetVerifiedOnly: e.target.checked })}
                />
                <FormLabel fontSize="xs" color="gray.600" mb={0} ml={2}>
                  {formData.targetVerifiedOnly ? "Yes" : "No"}
                </FormLabel>
              </FormControl>
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Company Type</Text>
              <Select
                mode="multiple" placeholder="Select company type"
                style={{ width: "100%" }}
                value={formData.sellerCompanyType}
                onChange={(values) => setFormData({ ...formData, sellerCompanyType: values })}
                options={[
                  { label: "Manufacturing", value: "Manufacturing" },
                  { label: "Trade", value: "Trade" },
                  { label: "Services / Solutions", value: "Services/Solutions" },
                  { label: "Others", value: "Others" },
                ]}
                allowClear
              />
            </Box>
          </GridItem>

          {formData.sellerCompanyType?.includes("Others") && (
            <GridItem colSpan={1}>
              <Box display="flex" flexDirection="column" gap="4px">
                <Text fontSize="xs" fontWeight="500">Company Type (Other)</Text>
                <Input height="28px" fontSize="xs" placeholder="Please describe"
                  name="sellerCompanyTypeOther" value={formData.sellerCompanyTypeOther ?? ""}
                  onChange={handleChange} />
              </Box>
            </GridItem>
          )}

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Product Type Tags</Text>
              <Select
                mode="tags"
                style={{ minHeight: "28px", height: "auto" }}
                placeholder="Enter Product Type Tags (max 50 chars each)"
                onChange={(value: string[]) => {
                  setFormData({ ...formData, productTypeTags: value.map((t: string) => t.toUpperCase()).filter((t: string) => t.length <= 50) });
                }}
                value={formData.productTypeTags}
                options={productTagOptions}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Box>
          </GridItem>

          {/* Claude: event dropdown moved next to product type tags 25-Apr-2026 */}
          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Event Name{isEdit ? "s" : ""}</Text>
              {isEdit ? (
                <Select
                  mode="multiple" showSearch optionFilterProp="label"
                  placeholder="Select Events"
                  style={{ width: "100%", minHeight: "28px" }}
                  value={sellerEventIds.length ? sellerEventIds : undefined}
                  options={eventData}
                  onChange={(values: string[]) => setSellerEventIds(values.filter(Boolean))}
                />
              ) : (
                <Select
                  showSearch optionFilterProp="label" placeholder="Select Event"
                  style={{ height: "28px" }}
                  value={formData.eventId}
                  options={[{ value: "", label: "Select Event" }, ...eventData]}
                  onChange={(value) => setFormData({ ...formData, eventId: value })}
                />
              )}
            </Box>
          </GridItem>

          <SectionLabel>Plans & Assignment</SectionLabel>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Subscription Plan</Text>
              <Select
                showSearch optionFilterProp="label"
                style={{ height: "28px" }}
                placeholder="Choose subscription plan"
                allowClear
                onChange={(value) => {
                  setFormData({ ...formData, subscriptionPlanId: value ?? null });
                  if (value && isEdit) {
                    setRenewInitialPlanId(value);
                    setIsRenewModalOpen(true);
                  }
                }}
                value={formData.subscriptionPlanId || undefined}
                options={subscriptionsData}
              />
            </Box>
          </GridItem>

          {/* Claude: plan values preview row — shown as soon as a plan is selected 25-Apr-2026 */}
          {selectedPlanPreview && (
            <GridItem colSpan={3}>
              <Box bg="blue.50" borderRadius="md" px={3} py={2} border="1px solid" borderColor="blue.100">
                <Flex gap={4} flexWrap="wrap" alignItems="center">
                  <Text fontSize="xs"><b>Value:</b> {selectedPlanPreview.currency} {selectedPlanPreview.subscriptionValue ?? "—"}</Text>
                  <Text fontSize="xs"><b>RFX Limit:</b> {selectedPlanPreview.rfxLimit || "∞"}</Text>
                  <Text fontSize="xs"><b>Pending:</b> {selectedPlanPreview.rfxResponsePendingLimit || "∞"}</Text>
                  {selectedPlanPreview.sellerTagsLimit != null && (
                    <Text fontSize="xs"><b>Tags:</b> {selectedPlanPreview.sellerTagsLimit || "∞"}</Text>
                  )}
                  {selectedPlanPreview.pendingMeetingsLimit != null && (
                    <Text fontSize="xs"><b>Meetings:</b> {selectedPlanPreview.pendingMeetingsLimit || "∞"}</Text>
                  )}
                  {selectedPlanPreview.digitalAssetsLimit != null && (
                    <Text fontSize="xs"><b>Assets:</b> {selectedPlanPreview.digitalAssetsLimit || "∞"}</Text>
                  )}
                  <Text fontSize="xs">
                    <b>Plan Active:</b>{" "}
                    <Text as="span" color={selectedPlanPreview.isActive ? "green.600" : "red.500"}>
                      {selectedPlanPreview.isActive ? "Yes" : "No"}
                    </Text>
                  </Text>
                </Flex>
              </Box>
            </GridItem>
          )}

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Reward Plan</Text>
              <Select
                showSearch optionFilterProp="label"
                style={{ height: "28px" }}
                placeholder="Choose reward plan"
                onChange={(value) => setFormData({ ...formData, rewardPlanId: value })}
                value={formData.rewardPlanId}
                options={rewardPlansData}
                allowClear
              />
            </Box>
          </GridItem>

          <SectionLabel>Contact Person</SectionLabel>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Person Name <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Person Name"
                name="personName" value={formData.personName} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Person Email <span style={{ color: "red" }}>*</span></Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Person Email"
                name="personEmail" value={formData.personEmail} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Person Phone</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Person Phone Number"
                name="personPhoneNumber" value={formData.personPhoneNumber} onChange={handleChange} />
            </Box>
          </GridItem>

          <GridItem colSpan={1}>
            <Box display="flex" flexDirection="column" gap="4px">
              <Text fontSize="xs" fontWeight="500">Person Designation</Text>
              <Input height="28px" fontSize="xs" placeholder="Enter Person Designation"
                name="personDesignation" value={formData.personDesignation} onChange={handleChange} />
            </Box>
          </GridItem>

          {isEdit && sellerId && (
            <>
              <SectionLabel>Digital Assets</SectionLabel>
              <GridItem colSpan={3}>
                <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
                  <AdminDigitalAssets sellerId={sellerId} userPlan={currentSellerPlan} />
                </Box>
              </GridItem>
            </>
          )}

          {isEdit && sellerId && (
            <>
              <SectionLabel>Subscription</SectionLabel>
              <GridItem colSpan={3}>
                <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
                  <AdminSellerSubscriptionSection sellerId={sellerId} />
                </Box>
              </GridItem>
            </>
          )}

        </Grid>

        {/* Claude: admin notes redesign — append-only log; new note mandatory on edit 27-04-2026 */}
        <Box mt={4}>
          <Box mb={1}>
            <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider">
              Admin Notes
            </Text>
            <Divider mt={1} borderColor="gray.200" />
          </Box>
          {isEdit ? (
            <>
              <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={2} mb={2} maxH="120px" overflowY="auto">
                {formData.adminNotes ? (
                  <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap">{formData.adminNotes}</Text>
                ) : (
                  <Text fontSize="xs" color="gray.400" fontStyle="italic">No previous notes.</Text>
                )}
              </Box>
              <Text fontSize="xs" fontWeight="500" mb={1}>New Note <span style={{ color: "red" }}>*</span></Text>
              <Textarea fontSize="xs" placeholder="Add note (required) — not visible to seller"
                value={newAdminNote} onChange={(e) => setNewAdminNote(e.target.value)} rows={3} resize="none" />
            </>
          ) : (
            <Textarea fontSize="xs" placeholder="Internal notes (not visible to seller)"
              name="adminNotes" value={formData.adminNotes ?? ""} onChange={handleChange} rows={3} resize="none" />
          )}
        </Box>

        <Box mt={4}>
          <Flex justifyContent="end" gap={2}>
            <Button h="28px" fontSize="xs" variant="outline" onClick={goBack}>Cancel</Button>
            <Button h="28px" fontSize="xs" background="#0E122F" color="white" px={3}
              _hover={{ background: "#2E7D32" }} isLoading={loading} onClick={handleSubmit}
            >
              Submit
            </Button>
          </Flex>
        </Box>
      </Box>
    </div>
  );
};

export default SellerEditPage;
